import { sparkToast } from '@spark';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import { allowedFormatsForTaskType } from '@/features/datasets/utils/datasetUploadFlow';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';

/**
 * Shared handling for OS files dragged/picked with an unsupported suffix on
 * the upload drop zones (wizard Data Collection cards, Data Overview empty
 * cards and Datasets/Benchmarks file boxes): partition the batch, surface an
 * inline card error and fire a bottom-right detailed toast.
 *
 * Every helper takes an optional `{ isAgentic }` — agentic tasks carry
 * nested `messages` trajectories, so they accept json/jsonl/parquet only.
 */

/** User-facing formats list, e.g. "JSON/JSONL" (matches the card subtitles). */
export function supportedFormatsShortLabel(taskType, { isAgentic = false } = {}) {
    if (isAgentic) return 'JSON/JSONL/Parquet';
    return taskType === TaskType.classification || isGenerativeTask(taskType)
        ? 'CSV/JSON/JSONL'
        : 'JSON/JSONL';
}

/**
 * Split picked/dropped files into `{ supported, unsupported }` by the task
 * type's allowed formats (suffix based).
 */
export function partitionFilesBySupport(fileList, taskType, { isAgentic = false } = {}) {
    const allowed = allowedFormatsForTaskType(taskType, { isAgentic });
    const supported = [];
    const unsupported = [];
    for (const file of Array.from(fileList || []).filter(Boolean)) {
        const format = detectFileFormat(file);
        if (format && allowed.has(format)) supported.push(file);
        else unsupported.push(file);
    }
    return { supported, unsupported };
}

/** Inline error message shown on the drop-zone card itself. */
export function unsupportedFileTypeCardMessage(taskType, options) {
    return `Unsupported file type. Please upload a ${supportedFormatsShortLabel(taskType, options)} file`;
}

/** Bottom-right detailed error toast for a batch of unsupported files. */
export function showUnsupportedFileTypeToast(unsupportedFiles, taskType, options) {
    const formats = supportedFormatsShortLabel(taskType, options);
    const content =
        unsupportedFiles.length === 1
            ? `"${unsupportedFiles[0].name}" is not supported. Please upload a ${formats} file.`
            : `${unsupportedFiles.length} files are not supported. Please upload ${formats} files.`;
    sparkToast.error('Unsupported file type', {
        content,
        detailed: true,
        duration: 5000,
    });
}
